import { Assets } from '@app/ui';
import { Country } from '@app/types';
import type { CheckoutAction } from '@checkout/types';

interface PaymentMethodMeta {
  src: string;
  srcSet: string;
  backgroundColor: string;
  alt: string;
  type: string;
  fauxCountryCode?: string;
  addressRequired?: boolean;
}

export interface StartedTwoStepCheckout {
  transactionId: string;
  providerPaymentId: string;
  callbackURL?: string;
  action?: CheckoutAction;
}

export interface PaymentProvider {
  name: string;
  key: string;
}

export interface BraintreePaymentProvider extends PaymentProvider {
  merchantAccountId: string;
}

export interface PaymentInstrument {
  provider: BraintreePaymentProvider;
  name: string;
  key: string;
  countries?: Country[];
}

export enum PaymentProviderType {
  Braintree = 'braintree',
  Citcon = 'citcon',
  Adyen = 'adyen',
}

export enum CardTypes {
  amazon = 'amazon',
  amex = 'american express',
  delta = 'delta',
  discover = 'discover',
  ebay = 'ebay',
  maestro = 'maestro',
  mastercard = 'mastercard',
  paypal = 'paypal',
  solo = 'solo',
  stripe = 'stripe',
  switch = 'switch',
  visa = 'visa',
  visaelectron = 'visaelectron',
}

export enum AdyenFlow {
  Vault = 'vault',
  Checkout = 'checkout',
}

export enum PaymentTransactionStatus {
  InProgress = 'IN_PROGRESS',
  Done = 'DONE',
  Failed = 'FAILED',
}

export enum CheckoutActionType {
  QRCode = 'QRCodeAction',
  Redirect = 'RedirectAction',
}

export enum BNPLPaymentMethods {
  Affirm = 'affirm',
  PaypalPayLater = 'Paypal BNPL',
}

export enum PaymentMethodType {
  CreditCard = 'CreditCard',
  Paypal = 'Paypal',
  BNPL = 'BNPL',
  APM = 'APM',
  LPM = 'LPM',
}

export interface GetBuyNowPayLaterEligibilityArgs {
  lowestAsk?: number;
  productCategory?: string;
  listingType?: string;
  paymentMethod?: BNPLPaymentMethods | string;
}

export type PaymentMethod =
  | 'paypal'
  | 'affirm'
  | 'bancontact'
  | 'giropay'
  | 'ideal'
  | 'klarna'
  | 'mybank'
  | 'alipay'
  | 'eps'
  | 'wechatpay'
  | 'sepa'
  | 'p24'
  | 'sofort'
  | 'alipay_hk'
  | 'upop'
  | 'kakaopay'
  | 'paypalpaylater';

type PaymentMethodDict = Record<PaymentMethod, PaymentMethodMeta>;

export const PAYMENT_METHODS: PaymentMethodDict = {
  paypal: {
    src: Assets.getPaymentMethodUrl('badge-paypal.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-paypal@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-paypal@3x.png')} 3x`,
    backgroundColor: '#ffc439',
    alt: 'Paypal',
    type: 'paypal',
  },
  paypalpaylater: {
    src: Assets.getPaymentMethodUrl('badge-paypal.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-paypal@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-paypal@3x.png')} 3x`,
    backgroundColor: '#ffc439',
    alt: 'PaypalPayLater',
    type: 'paypalPayLater',
  },
  affirm: {
    src: Assets.getPaymentMethodUrl('badge-affirm.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-affirm@2px.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-affirm@3x.png')} 3x`,
    backgroundColor: '#0fa0ea',
    alt: 'Affirm',
    type: 'affirm',
  },
  bancontact: {
    src: Assets.getPaymentMethodUrl('badge-bancontact.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-bancontact@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-bancontact@3x.png')} 3x`,
    backgroundColor: '#005498',
    alt: 'BanContact',
    type: 'bancontact',
    fauxCountryCode: 'BE',
  },
  giropay: {
    src: Assets.getPaymentMethodUrl('badge-giropay.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-giropay@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-giropay@3x.png')} 3x`,
    backgroundColor: '#01006b',
    alt: 'GiroPay',
    type: 'giropay',
    fauxCountryCode: 'DE',
  },
  ideal: {
    src: Assets.getPaymentMethodUrl('badge-ideal.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-ideal@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-ideal@3x.png')} 3x`,
    backgroundColor: '#cc0066',
    alt: 'Ideal',
    type: 'ideal',
    fauxCountryCode: 'NL',
  },
  klarna: {
    src: Assets.getPaymentMethodUrl('badge-klarna.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-klarna@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-klarna@3x.png')} 3x`,
    backgroundColor: '#eb6f93',
    alt: 'Klarna',
    type: 'klarna',
  },
  mybank: {
    src: Assets.getPaymentMethodUrl('badge-mybank.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-mybank@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-mybank@3x.png')} 3x`,
    backgroundColor: '#1b4b68',
    alt: 'MyBank',
    type: 'mybank',
    fauxCountryCode: 'IT',
  },
  alipay: {
    src: Assets.getPaymentMethodUrl('badge-alipay.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-alipay@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-alipay@3x.png')} 3x`,
    backgroundColor: '#00a0e9',
    alt: 'Alipay',
    type: 'alipay',
    fauxCountryCode: 'C2',
    addressRequired: false,
  },
  eps: {
    src: Assets.getPaymentMethodUrl('badge-eps.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-eps@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-eps@3x.png')} 3x`,
    backgroundColor: '#c8036f',
    alt: 'EPS',
    type: 'eps',
    fauxCountryCode: 'AT',
  },
  wechatpay: {
    src: Assets.getPaymentMethodUrl('badge-wechat-pay.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-wechat-pay@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-wechat-pay@3x.png')} 3x`,
    backgroundColor: '#1aad19',
    alt: 'WeChatPay',
    type: 'wechatpay',
  },
  sepa: {
    src: Assets.getPaymentMethodUrl('badge-sepa.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-sepa@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-sepa@3x.png')} 3x`,
    backgroundColor: '#0b4a8e',
    alt: 'SEPA',
    type: 'sepa',
    addressRequired: false,
  },
  p24: {
    src: Assets.getPaymentMethodUrl('badge-p-24.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-p-24@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-p-24@3x.png')} 3x`,
    backgroundColor: '#d03238',
    alt: 'P24',
    type: 'p24',
    fauxCountryCode: 'PL',
  },
  sofort: {
    src: Assets.getPaymentMethodUrl('badge-klarna.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-klarna@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-klarna@3x.png')} 3x`,
    backgroundColor: '#eb6f93',
    alt: 'Klarna',
    type: 'sofort',
    addressRequired: false,
  },
  alipay_hk: {
    src: Assets.getPaymentMethodUrl('badge-alipay-hk.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-alipay-hk@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-alipay-hk@3x.png')} 3x`,
    backgroundColor: '#01006b',
    alt: 'Alipay HK',
    type: 'alipay_hk',
    fauxCountryCode: 'HK',
    addressRequired: false,
  },
  upop: {
    src: Assets.getPaymentMethodUrl('badge-union-pay.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-union-pay@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-union-pay@3x.png')} 3x`,
    backgroundColor: '#01006b',
    alt: 'Union Pay',
    type: 'upop',
    fauxCountryCode: 'CN',
    addressRequired: false,
  },
  kakaopay: {
    src: Assets.getPaymentMethodUrl('badge-kakao-pay.png'),
    srcSet: `${Assets.getPaymentMethodUrl(
      'badge-kakao-pay@2x.png',
    )} 2x, ${Assets.getPaymentMethodUrl('badge-kakao-pay@3x.png')} 3x`,
    backgroundColor: '#01006b',
    alt: 'Kakao Pay',
    type: 'kakaopay',
    fauxCountryCode: 'KR',
    addressRequired: false,
  },
};
